import { AnyAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { ApiError, ApiResponse } from '../../../../../base/api';
import { Loading } from '../../../../../base/enums';
import { appActions } from '../../../../../base/redux/slices/appSlice';
import { RootState } from '../../../../../base/redux/store';
import { isEmptyObject } from '../../../../../base/utils';
import { settingsApi } from '../../api';
import { ChangeLoginStep, ChangePasswordStep, CreateLoginStep } from '../../enums';
import { settingsActions } from '../slice/settingsSlice';
import { __t } from '../../../../../base/utils/t';
import { workerAppActions } from '../../../../redux/slices/workerAppSlice';
import { baseErrorHandler } from '../../../../../base/redux/sagas/appSaga';
import { GetLoginResponse, GetMessengersResponse, Messenger } from '../../api/interfaces';

function* getCurrentLogin() {
    yield put(appActions.setLoading(Loading.App));

    try {
        const {
            data: { phone },
        }: ApiResponse<GetLoginResponse> = yield call(settingsApi.getLogin);

        yield put(settingsActions.setCurrentLogin(phone));
    } catch (e: unknown) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* changeLoginSubmit({ payload: callback }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    const { fields, step } = yield select((state: RootState) => state.workerAppSettings.changeLogin);

    try {
        if (step === ChangeLoginStep.Password) {
            yield call(settingsApi.changeLogin, { password: fields.password, validate: true });
            yield put(settingsActions.setChangeLoginStep(ChangeLoginStep.NewLogin));
            return;
        }

        yield call(settingsApi.changeLogin, {
            password: fields.password,
            phone: fields.phone,
            smsCode: fields.smsCode,
        });

        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Телефон успешно изменен'),
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );

        if (callback) {
            callback();
        }
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.smsVerificationNeeded && step !== ChangeLoginStep.Confirmation) {
                yield put(settingsActions.setChangeLoginStep(ChangeLoginStep.Confirmation));
                return;
            }
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(settingsActions.setChangeLoginErrors(e.errors));
                yield put(settingsActions.setChangeLoginShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* changePasswordSubmit({ payload: callback }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    const { fields, step } = yield select((state: RootState) => state.workerAppSettings.changePassword);

    try {
        if (step === ChangePasswordStep.OldPassword) {
            yield call(settingsApi.changePassword, { password: fields.password, validate: true });
            yield put(settingsActions.setChangePasswordStep(ChangePasswordStep.NewPassword));
            return;
        }

        yield call(settingsApi.changePassword, fields);

        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Пароль успешно сохранен'),
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );

        if (callback) {
            callback();
        }
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(settingsActions.setChangePasswordErrors(e.errors));
            yield put(settingsActions.setChangePasswordShouldScrollToError(true));
            return;
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* changeLoginSubmitChangeConfirmationValue({ payload: { newValue, recaptchaToken, callback } }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    try {
        const password: string = yield select(
            (state: RootState) => state.workerAppSettings.changeLogin.fields.password,
        );

        yield call(settingsApi.changeLogin, { password, phone: newValue, validate: true });
        yield put(settingsActions.setChangeLoginField({ id: 'phone', value: newValue }));
        yield put(workerAppActions.sendConfirmationCode(newValue, recaptchaToken));
        callback();
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(settingsActions.setChangeLoginErrors(e.errors));
                yield put(settingsActions.setChangeLoginShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* createLoginSubmit({ payload: callback }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    const { fields, step } = yield select((state: RootState) => state.workerAppSettings.createLogin);

    try {
        yield call(settingsApi.createLogin, fields);
        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Логин и пароль успешно созданы'),
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );

        if (callback) {
            callback();
        }
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.smsVerificationNeeded && step !== CreateLoginStep.Confirmation) {
                yield put(settingsActions.setCreateLoginStep(CreateLoginStep.Confirmation));
                return;
            }
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(settingsActions.setCreateLoginErrors(e.errors));
                yield put(settingsActions.setCreateLoginShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* createLoginSubmitChangeConfirmationValue({ payload: { newValue, recaptchaToken, callback } }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    try {
        const { password } = yield select((state: RootState) => state.workerAppSettings.createLogin.fields);

        yield call(settingsApi.createLogin, { phone: newValue, password, validate: true });
        yield put(settingsActions.setCreateLoginField({ id: 'phone', value: newValue }));
        yield put(workerAppActions.sendConfirmationCode(newValue, recaptchaToken));
        callback();
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(settingsActions.setCreateLoginErrors(e.errors));
            yield put(settingsActions.setCreateLoginShouldScrollToError(true));
            return;
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* getNotificationMessengers() {
    yield put(appActions.setLoading(Loading.App));
    try {
        const {
            data: { messengers },
        }: ApiResponse<GetMessengersResponse> = yield call(settingsApi.getMessengers);
        yield put(settingsActions.setNotificationMessengers(messengers));
    } catch (e: unknown) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* toggleNotificationsMessenger({ payload }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    const { notificationMessengers } = yield select((state: RootState) => state.workerAppSettings);

    try {
        const messenger = notificationMessengers.find((m: Messenger) => m.id === payload);
        if (messenger?.isActive) {
            yield call(settingsApi.removeMessenger, messenger.id);
        } else {
            yield window.open(messenger.subscribeLink, '_blank');
        }
        yield call(getNotificationMessengers);
    } catch (e: unknown) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

export function* settingsSaga() {
    yield takeLatest(settingsActions.getCurrentLogin, getCurrentLogin);

    yield takeLatest(settingsActions.createLoginSubmit, createLoginSubmit);
    yield takeLatest(
        settingsActions.createLoginSubmitChangeConfirmationValue,
        createLoginSubmitChangeConfirmationValue,
    );

    yield takeLatest(settingsActions.changeLoginSubmit, changeLoginSubmit);
    yield takeLatest(
        settingsActions.changeLoginSubmitChangeConfirmationValue,
        changeLoginSubmitChangeConfirmationValue,
    );
    yield takeLatest(settingsActions.changePasswordSubmit, changePasswordSubmit);

    yield takeLatest(settingsActions.getNotificationMessengers, getNotificationMessengers);
    yield takeLatest(settingsActions.toggleNotificationsMessenger, toggleNotificationsMessenger);
}
